/**
 * Utilities de formatage partagées
 */

import { GLOVO_CANCEL_REASON_LABELS } from "../constants";

/**
 * Libellé FR pour un code d'annulation Glovo (fallback: code brut)
 */
export function formatGlovoCancelReason(code: string | undefined | null): string {
  if (code == null || String(code).trim() === "") return "—";
  const key = String(code).trim();
  return GLOVO_CANCEL_REASON_LABELS[key] ?? key;
}

/**
 * Formate un prix en MAD
 */
export function formatPrice(price: number | undefined | null): string {
  if (price === undefined || price === null) return "0.00 MAD";
  return `${price.toFixed(2)} MAD`;
}

/**
 * Formate une date en format lisible
 */
export function formatDate(date: Date | any | undefined | null): string {
  if (!date) return "N/A";
  
  try {
    // Si c'est un Timestamp Firestore
    if (date.toDate && typeof date.toDate === "function") {
      date = date.toDate();
    }
    // Si c'est un objet sérialisé Firestore { _seconds, _nanoseconds }
    else if (date._seconds !== undefined) {
      date = new Date(date._seconds * 1000);
    }
    // Si c'est une string, la convertir
    else if (typeof date === "string") {
      date = new Date(date);
    }
    
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "N/A";
    }
    
    return new Intl.DateTimeFormat("fr-MA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
}

/**
 * Formate une date en format court (sans heure)
 */
export function formatDateShort(date: Date | any | undefined | null): string {
  if (!date) return "N/A";
  
  try {
    if (date.toDate && typeof date.toDate === "function") {
      date = date.toDate();
    }
    else if (date._seconds !== undefined) {
      date = new Date(date._seconds * 1000);
    }
    else if (typeof date === "string") {
      date = new Date(date);
    }
    
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "N/A";
    }
    
    return new Intl.DateTimeFormat("fr-MA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
}

/**
 * Formate une durée en minutes/heures
 */
export function formatDuration(minutes: number | undefined | null): string {
  if (minutes === undefined || minutes === null) return "N/A";
  
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}min`;
}

/**
 * Formate un nombre avec séparateur de milliers
 */
export function formatNumber(num: number | undefined | null): string {
  if (num === undefined || num === null) return "0";
  return new Intl.NumberFormat("fr-MA").format(num);
}

/**
 * Calcule le temps écoulé depuis une date (ex: "Il y a 5 min")
 */
export function timeAgo(date: Date | any | undefined | null): string {
  if (!date) return "N/A";
  
  try {
    if (date.toDate && typeof date.toDate === "function") {
      date = date.toDate();
    }
    else if (date._seconds !== undefined) {
      date = new Date(date._seconds * 1000);
    }
    else if (typeof date === "string") {
      date = new Date(date);
    }
    
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "N/A";
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) return `Il y a ${diffWeeks} sem`;
    
    const diffMonths = Math.floor(diffDays / 30);
    return `Il y a ${diffMonths} mois`;
  } catch (error) {
    console.error("Error calculating time ago:", error);
    return "N/A";
  }
}
